import { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Grid, TextField, MenuItem, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Alert, Divider, Chip,
} from '@mui/material';
import { Download as DownloadIcon, PlayArrow as RunIcon } from '@mui/icons-material';
import { api } from '../services/apiClient';
import { API_CONFIG, API_ENDPOINTS } from '../config/api';

interface EntityDef {
  value: string;
  label: string;
  filters: { key: string; label: string; type: string; options?: string[] }[];
}

const CustomReportPage = () => {
  const [entities, setEntities] = useState<EntityDef[]>([]);
  const [entity, setEntity] = useState('appointments');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [columns, setColumns] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [ran, setRan] = useState(false);

  useEffect(() => {
    api.get<{ data: EntityDef[] }>(API_ENDPOINTS.REPORT_BUILDER.ENTITIES)
      .then(res => setEntities(res.data ?? []))
      .catch(() => {});
  }, []);

  const currentEntityDef = entities.find(e => e.value === entity);

  const handleEntityChange = (val: string) => {
    setEntity(val);
    setActiveFilters({});
    setRows([]);
    setColumns([]);
    setRan(false);
  };

  const handleRun = async () => {
    setLoading(true);
    setError('');
    setRan(true);
    try {
      const filters: Record<string, string | number | null> = {};
      for (const [k, v] of Object.entries(activeFilters)) {
        if (v !== '' && v !== null) {
          const filterDef = currentEntityDef?.filters.find(f => f.key === k);
          filters[k] = filterDef?.type === 'number' ? Number(v) : v;
        }
      }
      const res = await api.post<{ data: Record<string, unknown>[]; count: number }>(
        API_ENDPOINTS.REPORT_BUILDER.RUN,
        { entity, date_from: dateFrom || undefined, date_to: dateTo || undefined, filters }
      );
      const data = res.data ?? [];
      setRows(data);
      setColumns(data.length > 0 ? Object.keys(data[0]) : []);
    } catch {
      setError('Failed to run report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCSV = async () => {
    const filters: Record<string, string | number | null> = {};
    for (const [k, v] of Object.entries(activeFilters)) {
      if (v !== '') {
        const filterDef = currentEntityDef?.filters.find(f => f.key === k);
        filters[k] = filterDef?.type === 'number' ? Number(v) : v;
      }
    }
    const payload = { entity, date_from: dateFrom || undefined, date_to: dateTo || undefined, filters, format: 'csv' };

    const token = localStorage.getItem('access_token');
    const response = await fetch(`${API_CONFIG.FULL_URL}${API_ENDPOINTS.REPORT_BUILDER.RUN}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    });
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${entity}-report.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatCell = (val: unknown): string => {
    if (val === null || val === undefined) return '-';
    if (typeof val === 'number') return String(val);
    const str = String(val);
    // Detect date strings like 2024-01-15 or ISO timestamps
    if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
      return new Date(str).toLocaleDateString();
    }
    return str;
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>Custom Report Builder</Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={3}>
            <TextField
              select fullWidth label="Report Type" value={entity}
              onChange={e => handleEntityChange(e.target.value)}
              size="small"
            >
              {entities.map(e => (
                <MenuItem key={e.value} value={e.value}>{e.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              fullWidth label="Date From" type="date" value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }} size="small"
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              fullWidth label="Date To" type="date" value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }} size="small"
            />
          </Grid>

          {/* Dynamic filters for the selected entity */}
          {currentEntityDef?.filters.map(f => (
            <Grid item xs={12} sm={2} key={f.key}>
              {f.type === 'select' && f.options ? (
                <TextField
                  select fullWidth label={f.label} size="small"
                  value={activeFilters[f.key] ?? ''}
                  onChange={e => setActiveFilters(p => ({ ...p, [f.key]: e.target.value }))}
                >
                  <MenuItem value="">All</MenuItem>
                  {f.options.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </TextField>
              ) : (
                <TextField
                  fullWidth label={f.label} size="small"
                  type={f.type === 'number' ? 'number' : 'text'}
                  value={activeFilters[f.key] ?? ''}
                  onChange={e => setActiveFilters(p => ({ ...p, [f.key]: e.target.value }))}
                />
              )}
            </Grid>
          ))}

          <Grid item xs={12} sm="auto" sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
            <Button
              variant="contained" startIcon={<RunIcon />}
              onClick={handleRun} disabled={loading}
            >
              {loading ? 'Running…' : 'Run Report'}
            </Button>
            {rows.length > 0 && (
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadCSV}>
                CSV
              </Button>
            )}
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && (
        <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
      )}

      {!loading && ran && (
        <>
          <Box display="flex" alignItems="center" gap={2} mb={1}>
            <Typography variant="subtitle1" fontWeight={600}>
              Results
            </Typography>
            <Chip label={`${rows.length} rows`} size="small" color={rows.length > 0 ? 'primary' : 'default'} />
          </Box>
          <Divider sx={{ mb: 2 }} />

          {rows.length === 0 ? (
            <Alert severity="info">No data found for the selected criteria.</Alert>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: 600, overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {columns.map(col => (
                      <TableCell key={col} sx={{ fontWeight: 700, textTransform: 'capitalize', whiteSpace: 'nowrap' }}>
                        {col.replace(/_/g, ' ')}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, idx) => (
                    <TableRow key={idx} hover>
                      {columns.map(col => (
                        <TableCell key={col} sx={{ whiteSpace: 'nowrap' }}>
                          {formatCell(row[col])}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
};

export default CustomReportPage;
